import { Bar, BarChart, CartesianGrid, Legend, Tooltip, XAxis, YAxis } from '../../../recharts';
import { TEST_RESULT } from './constants';

export const aggregateByExecutionIdPrefix = (validationTestResults) => {
  return validationTestResults.reduce((acc, testResultItem) => {
    const { executionId } = testResultItem;
    const parts = executionId.split('#');
    const executionIdPrefix = parts[0];

    acc[executionIdPrefix] = acc[executionIdPrefix]
      ? acc[executionIdPrefix].concat([testResultItem])
      : [testResultItem];

    return acc;
  }, {});
};

export const aggregateByFeatureName = (validationTestResults) => {
  return aggregateByField(validationTestResults, 'featureName');
};

export const aggregateByTimeframe = (validationTestResults) => {
  return aggregateByField(validationTestResults, 'timeFrame');
};

export const aggregateByMarketplace = (validationTestResults) => {
  return aggregateByField(validationTestResults, 'marketplace');
};

export const aggregateByRegion = (validationTestResults) => {
  return aggregateByField(validationTestResults, 'region');
};

export const aggregateByChannel = (validationTestResults) => {
  return aggregateByField(validationTestResults, 'channel');
};

export const aggregateByField = (validationTestResults, fieldKey) => {
  return validationTestResults.reduce((acc, testResultItem) => {
    const fieldValue = testResultItem[fieldKey] ? testResultItem[fieldKey].trim() : testResultItem[fieldKey];
    acc[fieldValue] = acc[fieldValue] ? acc[fieldValue].concat([testResultItem]) : [testResultItem];

    return acc;
  }, {});
};

export const countPassFail = (testResultItems) => {
  let pass = 0,
    fail = 0;
  for (let item of testResultItems) {
    if (itemFailed(item)) {
      ++fail;
    } else if (itemPassed(item)) {
      ++pass;
    }
  }

  return { pass, fail };
};

export const itemFailed = (testResultItem) => {
  return !itemPassed(testResultItem);
};

export const itemPassed = (testResultItem) => {
  const { result } = testResultItem;
  return (result || '').toLowerCase() === TEST_RESULT.PASS;
};

export const scenarioFailed = (testResultItem) => {
  return testResultItem.result.toLowerCase() === TEST_RESULT.FAIL;
};

export const anyStepFailed = (steps) => {
  return steps.some((step) => stepFailed(step));
};

export const stepFailed = (step) => {
  return step && step.result && step.result.toLowerCase() === TEST_RESULT.FAIL;
};

export const stepPassed = (step) => {
  return step && step.result && step.result.toLowerCase() === TEST_RESULT.PASS;
};

export const getTestResultContentByFilter = (testResults, filterItems, filterName) => {
  const filterIds = filterItems.map((item) => item.id);

  let byFilterId = {};

  for (let runKey of Object.keys(testResults)) {
    byFilterId[runKey] = {};
    for (let timeFrame of Object.keys(testResults[runKey])) {
      const testResultForTimeframe = testResults[runKey][timeFrame];
      for (let resultItem of testResultForTimeframe) {
        const elementsByFilterId = getElementsByFilterId(filterIds, filterName, resultItem.elements);

        for (let filterId of Object.keys(elementsByFilterId)) {
          if (!byFilterId[runKey][filterId]) {
            byFilterId[runKey][filterId] = {};
          }

          if (!byFilterId[runKey][filterId][timeFrame]) {
            byFilterId[runKey][filterId][timeFrame] = [];
          }

          if (elementsByFilterId[filterId] && elementsByFilterId[filterId].length) {
            byFilterId[runKey][filterId][timeFrame].push({
              ...resultItem,
              elements: elementsByFilterId[filterId],
            });
          }
        }
      }
    }
  }

  return byFilterId;
};

const getElementsByFilterId = (filterIds, filterName, testElements) => {
  let elementsByFilterId = {};
  for (let testElement of testElements) {
    let elementPlaced = false;
    for (let filterId of filterIds) {
      if (doesElementMatchFilter(testElement, filterName, filterId)) {
        if (!elementsByFilterId[filterId]) {
          elementsByFilterId[filterId] = [];
        }

        elementsByFilterId[filterId].push(testElement);
        elementPlaced = true;
        continue;
      }
    }

    if (!elementsByFilterId['unknown']) {
      elementsByFilterId['unknown'] = [];
    }

    if (!elementPlaced) {
      elementsByFilterId['unknown'].push(testElement);
    }
  }

  return elementsByFilterId;
};

const doesElementMatchFilter = (element, filterName, filterId) => {
  return new RegExp('\\b' + filterName + ' ' + filterId + '\\b', 'g').test(element.name);
};

export const getLabel = (id, items, notFoundLabel = 'None') => {
  const item = items.find((item) => item.id === id);
  return item ? item.label : notFoundLabel;
};

export const getChartByFilter = (testResultsByFilter, filterItems, keySortFunction) => {
  const sortedKeys = keySortFunction
    ? Object.keys(testResultsByFilter).sort(keySortFunction)
    : Object.keys(testResultsByFilter);
  const data = sortedKeys.map((filterKey) => {
    const testResults = [].concat.apply([], Object.values(testResultsByFilter[filterKey]));
    const count = countPassFail(testResults);

    return {
      name: getLabel(filterKey, filterItems),
      pass: count.pass,
      fail: count.fail,
    };
  });
  return getBarChart({ data, width: 900 });
};

export const getChartByKey = (testResultsByKey) => {
  const data = Object.keys(testResultsByKey).map((key) => {
    const count = countPassFail(testResultsByKey[key]);

    return {
      name: key.toUpperCase(),
      pass: count.pass,
      fail: count.fail,
    };
  });

  return getBarChart({ data, width: 900 });
};

const getBarChart = ({ data, height = 300, width = 600 }) => {
  return (
    <BarChart width={width} height={height} data={data} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
      <CartesianGrid strokeDasharray="3 3" />
      <XAxis dataKey="name" />
      <YAxis />
      <Tooltip />
      <Legend />
      <Bar dataKey="pass" fill="green" />
      <Bar dataKey="fail" fill="red" />
    </BarChart>
  );
};

export const getTitle = (executionIdPrefix) => {
  const dateOfRun = new Date(parseInt(executionIdPrefix)).toLocaleString('en-US');

  return (
    <div>
      <h2>Test Results (run on {dateOfRun} local time)</h2>
    </div>
  );
};

export const keySortFunction = (items) => {
  return (a, b) => {
    const itemA = items.find((item) => item.id === a);
    const itemB = items.find((item) => item.id === b);

    const indexA = items.indexOf(itemA);
    const indexB = items.indexOf(itemB);

    if (indexA < 0) {
      return +1;
    }
    if (indexB < 0) {
      return -1;
    }

    return indexA - indexB;
  };
};

export const getLatestExecutionIdPrefix = (validationTestResults) => {
  const executionIdPrefixes = validationTestResults.map((v) => v.executionId.split('#')[0]);
  return Math.max(...executionIdPrefixes);
};



// WEBPACK FOOTER //
// ./src/components/simulator/validation_test_results/utils.jsx